import { useLocale, useTranslation, type Locale } from '@mypoz/i18n';
import { Icon, cn } from '@mypoz/ui';

/**
 * A plain <select> on purpose: it is keyboard- and screen-reader-correct for free, and renders
 * as a native picker on mobile. Swap it for a Radix dropdown only when the design demands it.
 */
export function LanguageSwitcher({
  className,
  variant = 'select',
}: {
  className?: string;
  variant?: 'select' | 'segmented';
}) {
  const { t } = useTranslation();
  const { locale, locales, labels, setLocale } = useLocale();

  if (variant === 'segmented') {
    return (
      <fieldset className={className}>
        <legend className="mb-2 text-xs font-semibold tracking-[0.12em] text-muted uppercase">
          {t('language.label')}
        </legend>
        <div className="inline-flex overflow-hidden rounded-md border border-line" role="group">
          {locales.map((code) => (
            <button
              key={code}
              type="button"
              onClick={() => setLocale(code)}
              className={cn(
                'min-h-11 min-w-11 border-r border-line px-4 text-sm font-medium transition-colors last:border-r-0 focus-visible:z-10 focus-visible:outline-2 focus-visible:outline-brand',
                locale === code
                  ? 'bg-ink text-white'
                  : 'bg-surface text-muted hover:bg-surface-alt hover:text-ink',
              )}
              aria-pressed={locale === code}
            >
              {labels[code]}
            </button>
          ))}
        </div>
      </fieldset>
    );
  }

  return (
    <label className={className}>
      <span className="sr-only">{t('language.change')}</span>
      <span className="relative flex items-center">
        <Icon
          name="translate"
          size={18}
          className="pointer-events-none absolute left-2.5 text-muted"
        />
        <select
          value={locale}
          onChange={(e) => setLocale(e.target.value as Locale)}
          className="min-h-11 min-w-11 appearance-none rounded-md border border-line bg-surface py-2 pl-9 pr-8 text-sm text-ink focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand lg:min-h-0 lg:min-w-0"
        >
          {locales.map((code) => (
            <option key={code} value={code}>
              {labels[code]}
            </option>
          ))}
        </select>
        <Icon
          name="expand_more"
          size={18}
          className="pointer-events-none absolute right-2 text-muted"
        />
      </span>
    </label>
  );
}
